/**
 * Steel Fabrication Manager
 *
 * Shared logic behind the BIM-like Steel tab: creates the document elements
 * for Plates, Bolts and Welds, validates and applies parametric cuts
 * (Modifier, Cope, Miter, Cut Through) onto structural members, and derives
 * simple fabrication metrics (weight) for takeoff consistency.
 *
 * All public builders are pure with respect to the store: they RETURN element
 * payloads; tools decide when to commit them via bimStore.addElement.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import {
    Element,
    SteelPlateElement,
    SteelBoltElement,
    SteelWeldElement,
    SteelCutData,
} from './types';
import i18n from '../i18n';

// Density of structural steel, kg/m3 (S235..S355 practical constant).
const STEEL_DENSITY = 7850;

/** Categories a steel operation may legally target. */
export const STEEL_HOST_CATEGORIES = ['Structural Framing', 'Structural Columns'] as const;

export function isSteelHostElement(element: Element | undefined): boolean {
    if (!element) return false;
    return (STEEL_HOST_CATEGORIES as readonly string[]).includes(element.category);
}

/**
 * Resolve the start->end axis and cross-section of a framing/column element,
 * in world meters. Columns are synthesized as vertical members from their
 * location + height so cuts and bolts behave uniformly.
 */
export function resolveMemberFrame(element: Element): {
    start: THREE.Vector3;
    end: THREE.Vector3;
    width: number; // m
    depth: number; // m
} | null {
    const geom = element.geometryData as any;
    if (!geom) return null;

    if (geom.start && geom.end) {
        const start = new THREE.Vector3(...geom.start);
        const end = new THREE.Vector3(...geom.end);
        const profile = geom.profile;
        const width = profile ? Math.max((profile.width ?? profile.diameter ?? 100) / 1000, 0.03)
            : (geom.width ?? 0.2);
        const depth = profile ? Math.max((profile.depth ?? profile.height ?? profile.diameter ?? 100) / 1000, 0.03)
            : (geom.depth ?? 0.2);
        return { start, end, width, depth };
    }
    // Enhanced columns store basePoint/topPoint with mm profiles.
    if (geom.basePoint && geom.topPoint) {
        const start = new THREE.Vector3(...geom.basePoint);
        const end = new THREE.Vector3(...geom.topPoint);
        const profile = geom.profile;
        const width = profile ? Math.max((profile.width ?? profile.diameter ?? 300) / 1000, 0.05) : 0.3;
        const depth = profile ? Math.max((profile.depth ?? profile.width ?? 300) / 1000, 0.05) : 0.3;
        return { start, end, width, depth };
    }
    if (geom.location && typeof geom.height === 'number') {
        const start = new THREE.Vector3(geom.location[0], geom.location[1], geom.location[2] ?? 0);
        const end = start.clone();
        end.z += geom.height;
        const w = Math.max((geom.width ?? 0.3), 0.05);
        const d = Math.max((geom.depth ?? geom.width ?? 0.3), 0.05);
        return { start, end, width: w, depth: d };
    }
    return null;
}

export class SteelFabricationManager {

    // ==================== Plate ====================

    /**
     * Build a Steel Plates element from a boundary polygon drawn on a plane.
     * `points` are world-space 3D vertices of the sketch outline.
     */
    static createPlate(
        points: THREE.Vector3[],
        thicknessM: number,
        options: { materialName?: string; name?: string } = {}
    ): SteelPlateElement | null {
        if (points.length < 3) return null;

        // Fit a plane through the sketch points (Newell normal for stability).
        const normal = new THREE.Vector3();
        for (let i = 0; i < points.length; i++) {
            const cur = points[i];
            const nxt = points[(i + 1) % points.length];
            normal.x += (cur.y - nxt.y) * (cur.z + nxt.z);
            normal.y += (cur.z - nxt.z) * (cur.x + nxt.x);
            normal.z += (cur.x - nxt.x) * (cur.y + nxt.y);
        }
        if (normal.lengthSq() < 1e-10) return null;
        normal.normalize();

        const origin = points[0].clone();
        const xDir = points[1].clone().sub(origin);
        xDir.addScaledVector(normal, -xDir.dot(normal));
        if (xDir.lengthSq() < 1e-10) return null;
        xDir.normalize();
        const yDir = new THREE.Vector3().crossVectors(normal, xDir).normalize();

        const boundary: [number, number][] = points.map(p => {
            const v = p.clone().sub(origin);
            return [v.dot(xDir), v.dot(yDir)];
        });

        return {
            id: uuidv4(),
            category: 'Steel Plates',
            type: options.materialName ?? 'Steel Plate',
            parameters: {
                'Thickness': thicknessM * 1000,               // stored mm, BIM convention
                'Material': options.materialName ?? 'Steel - S355',
                'Elevation': origin.z,
                'Work Plane Normal': [normal.x, normal.y, normal.z],
            },
            constraints: [],
            geometryData: {
                boundary,
                origin: [origin.x, origin.y, origin.z],
                normal: [normal.x, normal.y, normal.z],
                thickness: thicknessM,
                // Local frame so the renderer can rebuild world positions.
                xDirection: [xDir.x, xDir.y, xDir.z],
            },
        };
    }

    // ==================== Bolts ====================

    /**
     * Place a rectangular bolt grid centered at `center`, axis pointing
     * through the connected plates. Auto-punches hole parameters onto the
     * connected elements (mirrors BIM behavior at a data level).
     */
    static createBoltGroup(
        center: THREE.Vector3,
        axis: THREE.Vector3,
        rowCount: number,
        columnCount: number,
        spacingM: number,
        connectedElementIds: string[],
        options: {
            standard?: string;
            diameterMm?: number;
            holeType?: string;
        } = {}
    ): SteelBoltElement | null {
        if (rowCount < 1 || columnCount < 1) return null;

        const axisN = axis.clone().normalize();
        // Stable reference: prefer world Z, then X, for building the grid plane.
        const ref = Math.abs(axisN.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
        const rowDir = new THREE.Vector3().crossVectors(axisN, ref).normalize();
        if (rowDir.lengthSq() < 1e-10) rowDir.set(1, 0, 0);
        const colDir = new THREE.Vector3().crossVectors(axisN, rowDir).normalize();

        return {
            id: uuidv4(),
            category: 'Steel Bolts',
            type: `${options.standard ?? 'A325'}: M${options.diameterMm ?? 20}`,
            parameters: {
                'Bolt Standard': options.standard ?? 'A325',
                'Bolt Diameter': options.diameterMm ?? 20,
                'Hole Type': options.holeType ?? 'Standard',
                'Rows': rowCount,
                'Columns': columnCount,
                'Spacing': spacingM * 1000,
                'Quantity': rowCount * columnCount,
            },
            constraints: connectedElementIds.map(id => ({
                type: 'JOIN' as const,
                targetId: id,
            })),
            geometryData: {
                position: [center.x, center.y, center.z],
                axis: [axisN.x, axisN.y, axisN.z],
                rowDirection: [rowDir.x, rowDir.y, rowDir.z],
                columnDirection: [colDir.x, colDir.y, colDir.z],
                rowCount,
                columnCount,
                rowSpacing: spacingM,
                columnSpacing: spacingM,
                connectedElementIds,
            },
        };
    }

    // ==================== Welds ====================

    static createWeld(
        path: THREE.Vector3[],
        primaryElementId: string,
        secondaryElementId: string | undefined,
        options: { location?: 'Shop' | 'Site'; sizeM?: number; symbol?: SteelWeldElement['geometryData']['symbol'] } = {}
    ): SteelWeldElement | null {
        if (path.length < 2) return null;
        const length = path.reduce((acc, p, i) => (i === 0 ? 0 : acc + p.distanceTo(path[i - 1])), 0);

        return {
            id: uuidv4(),
            category: 'Steel Welds',
            type: `${options.symbol ?? 'Fillet'} Weld (${options.location ?? 'Shop'})`,
            parameters: {
                'Weld Type': options.location ?? 'Shop',
                'Weld Symbol': options.symbol ?? 'Fillet',
                'Weld Size': (options.sizeM ?? 0.006) * 1000, // mm
                'Weld Length': length,
            },
            constraints: [{ type: 'JOIN', targetId: primaryElementId }],
            geometryData: {
                path: path.map(p => [p.x, p.y, p.z] as [number, number, number]),
                primaryElementId,
                secondaryElementId,
                location: options.location ?? 'Shop',
                size: options.sizeM ?? 0.006,
                symbol: options.symbol ?? 'Fillet',
            },
        };
    }

    // ==================== Parametric Cuts ====================

    static validateCutTarget(element: Element | undefined, kind: SteelCutData['kind']): {
        valid: boolean;
        message?: string;
    } {
        if (!element || !isSteelHostElement(element)) {
            return { valid: false, message: i18n.t('tools.notify.steelCutsApplyOnlyToStructural') };
        }
        if (!resolveMemberFrame(element)) {
            return { valid: false, message: i18n.t('tools.notify.selectedElementHasNoUsableMember') };
        }
        if (kind === 'Miter') {
            // Miter needs an intersecting partner; tools guarantee that by
            // picking two members, so nothing extra to check here.
        }
        return { valid: true };
    }

    /**
     * Append a cut to the host element and return the updates payload for
     * bimStore.updateElement. Existing cuts are preserved (BIM stacks them).
     */
    static buildCutUpdate(host: Element, cut: SteelCutData): { id: string; updates: Partial<Element> } {
        const cuts = [...(host.steelCuts ?? []), cut];
        return { id: host.id, updates: { steelCuts: cuts } };
    }

    /** Remove a cut by id; returns element updates (steelCuts may go empty). */
    static buildCutRemoval(host: Element, cutId: string): { id: string; updates: Partial<Element> } | null {
        if (!host.steelCuts?.length) return null;
        const cuts = host.steelCuts.filter(c => c.id !== cutId);
        return { id: host.id, updates: { steelCuts: cuts } };
    }

    // ==================== Metrics ====================

    /** Approximate weight of a plate group, kg (bounding-area based). */
    static plateWeight(plate: SteelPlateElement): number {
        const { boundary, thickness } = plate.geometryData;
        if (boundary.length < 3) return 0;
        // Shoelace area in the local plane.
        let area = 0;
        for (let i = 0; i < boundary.length; i++) {
            const [x1, y1] = boundary[i];
            const [x2, y2] = boundary[(i + 1) % boundary.length];
            area += x1 * y2 - x2 * y1;
        }
        return Math.abs(area / 2) * thickness * STEEL_DENSITY;
    }
}
